import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  CircularProgress,
  Alert,
  Autocomplete,
  Link,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BoltIcon from '@mui/icons-material/Bolt';
import NoteIcon from '@mui/icons-material/Note';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { Link as RouterLink } from 'react-router-dom';
import type { FHRequest } from '../../types';
import { managerApi, type AssistantOption } from '../../api/manager_api';
import { requestApi, type RequestDecline } from '../../api/request_api';

interface ManagerActionsCardProps {
  request: FHRequest;
  onRequestUpdate: () => void;
}

export function ManagerActionsCard({
  request,
  onRequestUpdate,
}: ManagerActionsCardProps) {
  // Quick actions state
  const [needsAttention, setNeedsAttention] = useState(request.needs_manager_attention || false);
  const [togglingAttention, setTogglingAttention] = useState(false);
  const [closingQuietly, setClosingQuietly] = useState(false);
  const [zeroingCredits, setZeroingCredits] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({ open: false, title: '', message: '', onConfirm: () => {} });

  // Unassign state
  const [unassigning, setUnassigning] = useState(false);

  // Pricing state
  const [price, setPrice] = useState<string>(request.price?.toFixed(2) || '0');
  const [credits, setCredits] = useState<string>(
    request.task_credits?.toString() || '1'
  );
  const [paid, setPaid] = useState(request.solver_paid || false);
  const [updatingPrice, setUpdatingPrice] = useState(false);

  // Reassign state
  const [availableAssistants, setAvailableAssistants] = useState<AssistantOption[]>([]);
  const [loadingAssistants, setLoadingAssistants] = useState(false);
  const [selectedAssistant, setSelectedAssistant] = useState<AssistantOption | null>(null);
  const [reassigning, setReassigning] = useState(false);

  // Internal note state
  const [godNote, setGodNote] = useState(request.god_note || '');
  const [savingNote, setSavingNote] = useState(false);

  // Assistants who hid this task from their Available list
  const [declines, setDeclines] = useState<RequestDecline[]>([]);

  // Error state
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Update local state when request changes
  useEffect(() => {
    setPrice(request.price?.toFixed(2) || '0');
    setCredits(request.task_credits?.toString() || '1');
    setPaid(request.solver_paid || false);
    setNeedsAttention(request.needs_manager_attention || false);
    setGodNote(request.god_note || '');
  }, [request.price, request.task_credits, request.solver_paid, request.needs_manager_attention, request.god_note]);

  // Load available assistants on mount
  useEffect(() => {
    const loadAssistants = async () => {
      setLoadingAssistants(true);
      try {
        const result = await managerApi.getAvailableAssistants();
        setAvailableAssistants(result.items);
      } catch (err) {
        console.error('Failed to load assistants:', err);
      } finally {
        setLoadingAssistants(false);
      }
    };
    loadAssistants();
  }, []);

  useEffect(() => {
    requestApi.listDeclines(request.key)
      .then((result) => setDeclines(result.items))
      .catch((err) => console.error('Failed to load declines:', err));
  }, [request.key]);

  const PRICE_PER_CREDIT = 3.0;

  const handleCreditsChange = (newCreditsValue: string) => {
    const newCredits = parseInt(newCreditsValue) || 0;
    if (newCredits > 0) {
      setPrice((PRICE_PER_CREDIT * newCredits).toFixed(2));
    }
    setCredits(newCreditsValue);
  };

  const handleUnassign = async () => {
    if (!request.claimed) return;
    setUnassigning(true);
    setError(null);
    setSuccess(null);
    try {
      await managerApi.unassignRequest(request.key);
      setSuccess('Request unassigned');
      onRequestUpdate();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to unassign');
    } finally {
      setUnassigning(false);
    }
  };

  const handleUpdatePricing = async () => {
    setUpdatingPrice(true);
    setError(null);
    setSuccess(null);
    try {
      await managerApi.updateRequestPricing(request.key, {
        price: parseFloat(price) || 0,
        credits: parseInt(credits) || 0,
        paid,
      });
      setSuccess('Pricing updated');
      onRequestUpdate();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update pricing');
    } finally {
      setUpdatingPrice(false);
    }
  };

  const handleReassign = async () => {
    if (!selectedAssistant) return;
    setReassigning(true);
    setError(null);
    setSuccess(null);
    try {
      await managerApi.reassignRequest(request.key, selectedAssistant.key);
      setSuccess(`Reassigned to ${selectedAssistant.name || selectedAssistant.email}`);
      setSelectedAssistant(null);
      onRequestUpdate();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reassign');
    } finally {
      setReassigning(false);
    }
  };

  const handleToggleAttention = async (newValue: boolean) => {
    setTogglingAttention(true);
    setNeedsAttention(newValue);
    setError(null);
    setSuccess(null);
    try {
      await managerApi.setManagerAttention(request.key, newValue);
      setSuccess(newValue ? 'Marked as needing attention' : 'Attention flag cleared');
      onRequestUpdate();
    } catch (err) {
      setNeedsAttention(!newValue); // Revert on error
      setError(err instanceof Error ? err.message : 'Failed to update attention flag');
    } finally {
      setTogglingAttention(false);
    }
  };

  const handleCloseQuietly = async () => {
    setClosingQuietly(true);
    setError(null);
    setSuccess(null);
    try {
      await managerApi.closeQuietly(request.key);
      setSuccess('Request closed without notifying client');
      onRequestUpdate();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to close request');
    } finally {
      setClosingQuietly(false);
      setConfirmDialog({ ...confirmDialog, open: false });
    }
  };

  const handleZeroCredits = async () => {
    setZeroingCredits(true);
    setError(null);
    setSuccess(null);
    try {
      await managerApi.updateRequestPricing(request.key, {
        credits: 0,
      });
      setSuccess('Credits set to zero');
      onRequestUpdate();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to zero credits');
    } finally {
      setZeroingCredits(false);
      setConfirmDialog({ ...confirmDialog, open: false });
    }
  };

  const handleArchive = async () => {
    setArchiving(true);
    setError(null);
    setSuccess(null);
    try {
      await managerApi.archiveRequest(request.key);
      setSuccess('Request archived');
      onRequestUpdate();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to archive request');
    } finally {
      setArchiving(false);
      setConfirmDialog({ ...confirmDialog, open: false });
    }
  };

  const handleSaveNote = async () => {
    setSavingNote(true);
    setError(null);
    setSuccess(null);
    try {
      await managerApi.updateManagerNote(request.key, godNote);
      setSuccess('Internal note saved');
      onRequestUpdate();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save note');
    } finally {
      setSavingNote(false);
    }
  };

  const openConfirmDialog = (
    title: string,
    message: string,
    onConfirm: () => void
  ) => {
    setConfirmDialog({ open: true, title, message, onConfirm });
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <AdminPanelSettingsIcon color="primary" />
          <Typography variant="h6">Manager Actions</Typography>
        </Box>

        <Link
          href={`https://www.fancyhands.com/admin/request/${request.datastore_key}`}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}
        >
          View this on old site <OpenInNewIcon fontSize="small" />
        </Link>

        {declines.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <VisibilityOffIcon fontSize="small" color="action" />
              <Typography variant="subtitle2">
                Hidden by {declines.length} assistant{declines.length === 1 ? '' : 's'}
              </Typography>
              <Link component={RouterLink} to="/manager/declines" variant="caption" sx={{ ml: 'auto' }}>
                All declines
              </Link>
            </Box>
            {declines.map((d) => (
              <Typography key={d.key} variant="body2" color="text.secondary">
                {d.user_name}: {d.reason_label} · {new Date(d.date_created).toLocaleDateString()}
              </Typography>
            ))}
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Quick Actions Section */}
        <Accordion disableGutters elevation={0} sx={{ bgcolor: 'transparent', '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BoltIcon fontSize="small" color="action" />
              <Typography variant="subtitle2">Quick Actions</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 0 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {/* Toggle Manager Attention */}
              <FormControlLabel
                control={
                  <Switch
                    checked={needsAttention}
                    onChange={(e) => handleToggleAttention(e.target.checked)}
                    disabled={togglingAttention}
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2">
                    Needs Manager Attention
                    {togglingAttention && (
                      <CircularProgress size={12} sx={{ ml: 1, verticalAlign: 'middle' }} />
                    )}
                  </Typography>
                }
              />

              {/* Close Without Notifying */}
              <Button
                variant="outlined"
                color="warning"
                size="small"
                disabled={closingQuietly || request.status >= 20}
                onClick={() =>
                  openConfirmDialog(
                    'Close Without Notifying',
                    'This will close the request without sending any notification to the client. Continue?',
                    handleCloseQuietly
                  )
                }
                startIcon={closingQuietly ? <CircularProgress size={16} /> : null}
                fullWidth
              >
                {closingQuietly ? 'Closing...' : 'Close Without Notifying Client'}
              </Button>

              {/* Zero Credits */}
              <Button
                variant="outlined"
                color="warning"
                size="small"
                disabled={zeroingCredits || request.task_credits === 0}
                onClick={() =>
                  openConfirmDialog(
                    'Zero Credits',
                    'This will set the task credits to 0. Continue?',
                    handleZeroCredits
                  )
                }
                startIcon={zeroingCredits ? <CircularProgress size={16} /> : null}
                fullWidth
              >
                {zeroingCredits ? 'Zeroing...' : 'Zero Credits'}
              </Button>

              {/* Archive Task */}
              {!request.archived && (
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  disabled={archiving}
                  onClick={() =>
                    openConfirmDialog(
                      'Archive Task',
                      'This will archive the request and set its status to archived. Continue?',
                      handleArchive
                    )
                  }
                  startIcon={archiving ? <CircularProgress size={16} /> : null}
                  fullWidth
                >
                  {archiving ? 'Archiving...' : 'Archive Task'}
                </Button>
              )}
              {request.archived && (
                <Typography variant="caption" color="text.secondary">
                  This task is already archived
                </Typography>
              )}
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Unassign Section */}
        <Accordion disableGutters elevation={0} sx={{ bgcolor: 'transparent', '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonOffIcon fontSize="small" color="action" />
              <Typography variant="subtitle2">Unassign</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 0 }}>
            <Button
              variant="outlined"
              color="warning"
              size="small"
              disabled={!request.claimed || unassigning}
              onClick={handleUnassign}
              startIcon={unassigning ? <CircularProgress size={16} /> : null}
              fullWidth
            >
              {unassigning ? 'Unassigning...' : 'Unassign Assistant'}
            </Button>
            {!request.claimed && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Request is not currently assigned
              </Typography>
            )}
          </AccordionDetails>
        </Accordion>

        {/* Pricing Section */}
        <Accordion disableGutters elevation={0} sx={{ bgcolor: 'transparent', '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AttachMoneyIcon fontSize="small" color="action" />
              <Typography variant="subtitle2">Pricing</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 0 }}>
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <TextField
                label="Price"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                size="small"
                type="number"
                inputProps={{ step: '0.01', min: '0' }}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Credits"
                value={credits}
                onChange={(e) => handleCreditsChange(e.target.value)}
                size="small"
                type="number"
                inputProps={{ step: '1', min: '0' }}
                sx={{ flex: 1 }}
              />
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={paid}
                  onChange={(e) => setPaid(e.target.checked)}
                  size="small"
                />
              }
              label="Paid"
              sx={{ mb: 1 }}
            />
            <Button
              variant="outlined"
              size="small"
              disabled={updatingPrice}
              onClick={handleUpdatePricing}
              startIcon={updatingPrice ? <CircularProgress size={16} /> : null}
              fullWidth
            >
              {updatingPrice ? 'Updating...' : 'Update Pricing'}
            </Button>
          </AccordionDetails>
        </Accordion>

        {/* Reassign Section */}
        <Accordion disableGutters elevation={0} sx={{ bgcolor: 'transparent', '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SwapHorizIcon fontSize="small" color="action" />
              <Typography variant="subtitle2">
                {request.claimed ? 'Reassign' : 'Assign'}
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 0 }}>
            <Autocomplete
              options={availableAssistants}
              loading={loadingAssistants}
              value={selectedAssistant}
              onChange={(_, value) => setSelectedAssistant(value)}
              getOptionLabel={(option) =>
                option.name ? `${option.name} (${option.email})` : option.email
              }
              isOptionEqualToValue={(option, value) => option.key === value.key}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Assistant"
                  size="small"
                  placeholder="Search by name or email"
                />
              )}
              sx={{ mb: 1 }}
            />
            <Button
              variant="outlined"
              size="small"
              disabled={!selectedAssistant || reassigning}
              onClick={handleReassign}
              startIcon={reassigning ? <CircularProgress size={16} /> : null}
              fullWidth
            >
              {reassigning
                ? 'Reassigning...'
                : request.claimed
                  ? 'Reassign'
                  : 'Assign'}
            </Button>
          </AccordionDetails>
        </Accordion>

        {/* Internal Note Section */}
        <Accordion disableGutters elevation={0} sx={{ bgcolor: 'transparent', '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <NoteIcon fontSize="small" color="action" />
              <Typography variant="subtitle2">Internal Note</Typography>
              {request.god_note && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                  (has note)
                </Typography>
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              This note is visible to assistants but not to clients.
            </Typography>
            <TextField
              value={godNote}
              onChange={(e) => setGodNote(e.target.value)}
              multiline
              rows={3}
              placeholder="Add an internal note for assistants..."
              size="small"
              fullWidth
              sx={{ mb: 1 }}
            />
            <Button
              variant="outlined"
              size="small"
              disabled={savingNote}
              onClick={handleSaveNote}
              startIcon={savingNote ? <CircularProgress size={16} /> : null}
              fullWidth
            >
              {savingNote ? 'Saving...' : 'Save Note'}
            </Button>
          </AccordionDetails>
        </Accordion>
      </CardContent>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
      >
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirmDialog.message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}>
            Cancel
          </Button>
          <Button onClick={confirmDialog.onConfirm} color="warning" autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
